import { useRealtimeKitClient } from '@cloudflare/realtimekit-react'
import {
  defaultConfig,
  registerAddons,
  RtkMeeting,
  RtkRecordingIndicator,
} from '@cloudflare/realtimekit-react-ui'
import ChatHostControl from '@cloudflare/realtimekit-ui-addons/chat-host-control'
import HandRaise from '@cloudflare/realtimekit-ui-addons/hand-raise'
import VideoBackground from '@cloudflare/realtimekit-ui-addons/video-background'
import RealtimeKitVideoBackgroundTransformer from '@cloudflare/realtimekit-virtual-background'
import LoadingSpinner from '@components/LoadingSpinner'
import { ApiService } from '@services/ApiService'
import { useEffect, useState } from 'react'

interface Props {
  token: string
  kawaconnVideoToken: string
}

// Reference: https://dyte.io/blog/building-timed-meetings
const MaMinutesDuration = 120 // 120 minutes

const KawaconnMeeting = (props: Props) => {
  const [meeting, initMeeting] = useRealtimeKitClient(null)
  const [meetingConfig, setMeetingConfig] = useState(defaultConfig)

  useEffect(() => {
    const searchParams = new URL(window.location.href).searchParams
    const authToken = props.token ?? searchParams.get('authToken')
    if (!authToken) {
      alert(
        "An authToken wasn't passed, please pass an authToken in the URL query to join a meeting."
      )
      return
    }

    initMeeting({
      authToken: authToken,
      defaults: {
        audio: false,
        video: false,
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /*
    Addons are registered from their own effect, keyed on `meeting`, and never
    in the render commit that first hands `meeting` to <RtkMeeting />.
    rtk-meeting turns loadConfigFromPreset on for itself, and its
    meetingChanged watcher then overwrites this.config with a config generated
    from the tenant preset. A config supplied in the same commit as `meeting`
    is thrown away along with every addon registered into it — that is what
    silently removed the video background "Effects" button from the control
    bar. Setting it here lands it in a later commit, where it survives.
  */
  useEffect(() => {
    if (!meeting) {
      return undefined
    }

    let isCancelled = false
    let videoBackground = null
    const closeMeetingTimers: ReturnType<typeof setTimeout>[] = []

    const onParticipantJoined = () => {
      closeMeetingTimers.push(
        setTimeout(() => {
          ApiService.VideoMeeting.CloseMeeting(
            meeting.meta.meetingId,
            props.kawaconnVideoToken
          )
        }, MaMinutesDuration * 60 * 1000)
      )
    }

    const registerMeetingAddons = async () => {
      if (!RealtimeKitVideoBackgroundTransformer.isSupported()) {
        // The addon drops itself out of the control bar and returns the config
        // untouched — no error, no button — whenever segmentation is
        // unavailable: no WebGL context, iOS (any browser), or Safari < 17.
        console.warn(
          '[VideoMeeting] Video background is unsupported in this browser, the Effects button will not be rendered.'
        )
      }

      videoBackground = await VideoBackground.init({
        meeting,
        segmentationConfig: {
          pipeline: 'canvas2dCpu', // 'webgl2' | 'canvas2dCpu'
        },
        // Self-hosted virtual-background images (ICS_Frontend/public/backgrounds).
        // Replaces assets.dyte.io URLs, which stop serving after Dyte EOL (30/06/2026).
        // Same-origin assets also avoid canvas CORS tainting in the segmentation pipeline.
        images: [
          '/backgrounds/bg_2.jpg',
          '/backgrounds/bg_3.jpg',
          '/backgrounds/bg_4.jpg',
          '/backgrounds/bg_5.jpg',
          '/backgrounds/bg_6.jpg',
          '/backgrounds/bg_7.jpg',
        ],
        modes: ['blur', 'virtual'],
        onVideoBackgroundUpdate: ({ backgroundMode, backgroundURL }) => {
          console.warn('[VideoMeeting] Video background changed', {
            backgroundMode,
            backgroundURL,
          })
        },
      })

      const handRaise = await HandRaise.init({
        meeting,
        canRaiseHand: true,
        canManageRaisedHand: true,
        // handRaiseIcon: '<svg>...</svg>', // optional, Replace it with proper SVG
      })

      const chatHostControl = await ChatHostControl.init({
        meeting,
        hostPresets: ['webinar_presenter'],
        targetPresets: ['webinar_viewer'],
        addActionInParticipantMenu: true,
      })

      if (isCancelled) {
        return
      }

      setMeetingConfig(
        registerAddons([videoBackground, handRaise, chatHostControl], meeting)
      )
    }

    registerMeetingAddons().catch(error => {
      // Anything thrown in here used to reject an un-awaited promise inside a
      // timer, leaving the meeting with no addons and nothing in the console.
      console.error('[VideoMeeting] Failed to register meeting addons', error)
    })

    meeting.participants.joined.on('participantJoined', onParticipantJoined)

    return () => {
      isCancelled = true
      closeMeetingTimers.forEach(clearTimeout)
      meeting.participants.joined.removeListener(
        'participantJoined',
        onParticipantJoined
      )
      videoBackground?.unregister?.().catch(() => undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meeting])

  return (
    <>
      {/*
        Permission-denied UI is handled internally by <RtkMeeting /> via its
        dialog-manager, which only shows the "Mic Permission denied" message
        when states.activePermissionsMessage.enabled is true (i.e. on a real
        DENIED/SYSTEM_DENIED event). Do NOT render <RtkPermissionsMessage />
        standalone here: it is a purely presentational component that always
        renders the denied message whenever mounted, so it flashed on open
        before RtkMeeting took over. See dialog-manager gating.
      */}
      {!meeting ? (
        <div className="w-full h-screen flex items-center justify-center">
          <LoadingSpinner spinning size="large" />
        </div>
      ) : (
        <>
          <RtkRecordingIndicator
            title="Recording"
            color="blue"
            meeting={meeting}
          >
            Video is recorded
          </RtkRecordingIndicator>
          <RtkMeeting meeting={meeting} config={meetingConfig} />
        </>
      )}
    </>
  )
}

export default KawaconnMeeting
